import { cloneDeep, flow } from 'lodash';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import { RULE_TYPES } from '../RuleBuilder/constants';
import { transformSearchQueries } from '../RuleBuilder/hooks/useRuleBuilder.utils';
import { ChildRule, Rule, RuleType, Rules } from '../RuleBuilder/types';
import {
  EXIT_RULE_TYPES,
  EXIT_RULE_TYPES_TO_RULE_TYPES,
  RULE_TYPES_TO_EXIT_RULE_TYPES,
} from './constants';
import { ExitRule, ExitRuleCombo, ExitRuleType, ExitRulesResponse } from './types';

const transformEventChildRules = (childRule: ChildRule) => ({
  journeyConversionRuleMetadata: childRule.journeyConversionRuleMetadata,
  ruleType: RULE_TYPES_TO_EXIT_RULE_TYPES[childRule.ruleType],
  id: childRule.id,
  searchQuery: childRule.searchQuery,
  ...(childRule.ruleType === RULE_TYPES.SYSTEM_EVENT && {
    systemEventName: childRule.eventName,
  }),
  ...((childRule.ruleType === RULE_TYPES.CUSTOM_EVENT ||
    childRule.ruleType === RULE_TYPES.CONVERSION_MET) && {
    customEventName: childRule.eventName,
  }),
  ...(childRule.ruleType === RULE_TYPES.USER_PROPERTY && {
    userField: childRule.userField,
  }),
});
/*
  Don't expect the keys and values used in the query to match the mutation.
*/
export const transformExitRulesForMutation = (exitRules: Rules) => {
  const transformedRules = exitRules.map((rule) => {
    const { childRules } = rule;
    if (isEmpty(childRules)) return undefined; // to be compacted
    const transformedRule =
      childRules.length === 1
        ? {
            ...transformEventChildRules(childRules[0]),
          }
        : ({
            ruleType: 'ExitRuleCombo',
            childRules: childRules.map(transformEventChildRules),
            combinator: 'And',
            id: rule.id,
          } as ExitRule);
    // function only called if no errors, therefore need to set cloning flag to false
    return {
      ...transformedRule,
      containsCloningError: false,
      sendToJourneyMetadata: rule?.sendToJourneyMetadata,
    };
  });
  return compact(transformedRules);
};

const transformTypename = (childRule: ExitRule) => {
  const newChildRule = cloneDeep(childRule);
  // we want to normalize rule types. i.e. ComboExitRule -> ComboRule
  const childRuleType = childRule.__typename as ExitRuleType;
  const ruleType = EXIT_RULE_TYPES_TO_RULE_TYPES[childRuleType] as RuleType;
  (newChildRule.ruleType as RuleType) = ruleType;
  delete newChildRule.__typename;
  return newChildRule;
};

const transformEventChildRule = (childRule: ExitRule) => {
  if (childRule.ruleType !== RULE_TYPES.SYSTEM_EVENT) return childRule;
  const newChildRule = cloneDeep(childRule);
  newChildRule.eventName = childRule.eventType;
  delete newChildRule.eventType;
  return newChildRule;
};

const transformChildRule = (childRule: ExitRule, containsCloningError: boolean) => {
  const transform = flow([transformTypename, transformSearchQueries, transformEventChildRule]);
  return transform({ ...childRule, containsCloningError });
};

const transformChildRules = (childRules: ExitRule[], containsCloningError: boolean) => {
  return childRules.map((cr) => transformChildRule(cr, containsCloningError));
};

export function transformExitRulesResponse(exitRulesResponse: ExitRulesResponse) {
  /*
  desired output:
   {
    rules: [{
      (rule block 1)
      combinator: 'And' // because multiple rules
      childRules: [
        {
          ruleType: '. . .',
          searchQuery: { . . . }
        },
        {
          ruleType: '. . .',
          searchQuery: { . . . }
        }
      ]
    },
    {
      (rule block 2)
      combinator: 'And' // because only one rule
      childRules: [ // even though only 1 at the moment!
        {
          ruleType: '. . .',
          searchQuery: { . . . }
        },
      ]
    }]
  }
    */
  let rules = exitRulesResponse?.exitRules?.reduce((acc: Rule[], exitRule: ExitRule) => {
    if (exitRule.__typename === EXIT_RULE_TYPES.COMBO_RULE) {
      acc.push({
        id: exitRule.id,
        combinator: 'And',
        enabledDate: exitRule.enabledDate,
        archivedDate: exitRule.archivedDate,
        childRules: transformChildRules(
          (exitRule as ExitRuleCombo).rules,
          exitRule?.containsCloningError === true,
        ) as ChildRule[],
        ruleType: RULE_TYPES.COMBO_RULE as RuleType, // ruleType saves as this exact string
        containsCloningError: exitRule?.containsCloningError === true,
        sendToJourneyMetadata: exitRule?.sendToJourneyMetadata,
      });
    } else {
      acc.push({
        id: exitRule.id,
        enabledDate: exitRule.enabledDate,
        archivedDate: exitRule.archivedDate,
        combinator: 'And', // here in case we want to change it to ExitRuleCombo later
        childRules: transformChildRules([exitRule], exitRule?.containsCloningError === true),
        containsCloningError: exitRule?.containsCloningError === true,
        sendToJourneyMetadata: exitRule?.sendToJourneyMetadata,
      });
    }
    return acc;
  }, []);

  if (!rules || rules.length === 0) {
    rules = [
      {
        combinator: 'And',
        childRules: [],
      },
    ];
  }

  return {
    rules,
  };
}
